import { memo, useState } from 'react';
import { useTimeout } from 'ahooks';

import { Box, Divider, VerticalNav, VisuallyHidden, ZillowLogo } from '@zillow/constellation-10';
import {
  IconArchiveFilled,
  IconArchiveOutline,
  IconBankFilled,
  IconBankOutline,
  IconCalculatorFilled,
  IconCalculatorOutline,
  IconCreditCardFilled,
  IconCreditCardOutline,
  IconDollarSignCircleFilled,
  IconDollarSignCircleOutline,
  IconEditFormFilled,
  IconEditFormOutline,
  IconGridFilled,
  IconGridOutline,
  IconHammerFilled,
  IconHammerOutline,
  IconHomeTypeFilled,
  IconHomeTypeOutline,
  IconMessageFilled,
  IconMessageOutline,
  IconTrendingFilled,
  IconTrendingOutline,
  IconUserFilled,
  IconUserGroupFilled,
  IconUserGroupOutline,
  IconUserOutline,
  IconYardSignFilled,
  IconYardSignOutline,
} from '@zillow/constellation-icons';

import { PathsManifest } from '@zg-rentals/paths-manifest';
import { useRentalManagerUserTrackingV2WebApiHandler_event } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { RentalManagerUserTrackingEvent } from '@zg-rentals/ts-rental-manager-api-client';

import { getNavDisplays, getPaymentsPath } from '../../utils/displays';
import { getMaintenanceNavState } from '../../utils/maintenanceNavState';
import { getPortfolioNavState } from '../../utils/portfolioNavState';
import HeaderNavigationClickstreamEvents from '../../utils/clickstream';
import {
  CSTriggerSourceFromHeaderTitlesMap,
  HeaderTitles,
  LESSEN_MAINTENANCE_CS_TRIGGER_SOURCE,
  LESSEN_MAINTENANCE_NEW_TAG_COOKIE,
  NAV_NEW_TAG_MAX_AGE_DAYS,
} from '../../constants';
import { useLessenMaintenanceNavState } from '../../hooks/useLessenMaintenanceNavState';
import { isListingCentricViewEnabled } from '../../utils/listingCentricView';

import AccountMenu from './AccountMenu';
import AdaptiveAnchor from '../AdaptiveAnchor';
import LogoDesktop from '../Logo/LogoDesktop';
import NavigationItem from '../NavigationItem';
import NavigationSkeleton from '../NavigationSkeleton';
import NotificationCenter from '../NotificationCenter';
import PlusIconPropertyMenu from '../PlusIconPropertyMenu';

import type { Props as HeaderNavigationProps } from '../HeaderNavigation';

const NAV_SKELETON_MAX_WAIT_MS = 3000;
import type { User } from '../../types';

export type Props = Partial<HeaderNavigationProps> & {
  // ZRM-16458: when rendered alongside the new IA TopNavBar, suppress the logo,
  // alerts, and account menu here since they live in the top bar instead.
  topBar?: boolean;
};

const VerticalNavigation = (props: Props) => {
  const [isAlertDropdownOpen, setIsAlertDropdownOpen] = useState(false);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const [hasNavWaitExpired, setHasNavWaitExpired] = useState(false);
  useTimeout(() => setHasNavWaitExpired(true), NAV_SKELETON_MAX_WAIT_MS);

  const { displays } = getNavDisplays(props);

  const {
    /**
     * altLogo true - zrm logo
     * altLogo false - zillow logo
     */
    altLogo = true,
    brand = 'zillow',
    pathname = '',
    reactRouterLink: v4Link,
    userModel = {} as User,
    escapeAllLinks = false,
    topBar = false,
    isListingsContext = false,
  } = props;

  const { features = {}, loggedIn, paymentsAcceptedCount, hasSeenNewPaymentsPage } = userModel;

  const isListingsNavEnabled = isListingCentricViewEnabled(userModel);
  const { canViewClaimFeedListing: canAccessAddFeedListingsModal = false } = features as User['features'];

  const {
    displayAnalytics,
    displayMainBanking,
    displayMainBilling,
    displayMainLeadManagement,
    displayMainLeads,
    displayMainLessenMaintenance,
    displayMainMaintenanceTracker,
    displayMainMessages,
    displayMainMyProperties,
    displayMainNotificationCenter,
    displayMainPayments,
    displayOnboardingTasks,
    displayUserManagement,
  } = displays;

  const { isEnabled: isLessenMaintenanceEnabled, isLoading: isLessenMaintenancePending } = useLessenMaintenanceNavState(
    {
      loggedIn,
      override: displayMainLessenMaintenance,
    },
  );

  // Pass link component from either react-router or next-js
  const Link = v4Link;

  const useAbsolutePaths = !props.useRelativePaths;
  const paths = new PathsManifest('/rental-manager', brand, useAbsolutePaths, props.host).getAllPaths();

  const {
    adManagementPage,
    analyticsOverview,
    analyticsPage,
    banking,
    bankingPage,
    billingDue,
    billingPage,
    bookkeeping,
    bookkeepingPage,
    dashboardPage,
    inboxPage,
    leadManagementPage,
    leadsPage,
    lessenMaintenancePage,
    listingsPage,
    maintenanceTrackerPage,
    payments,
    paymentsAggregatePage,
    paymentsGetStartedPage,
    propertiesPage,
    rentPayment,
    rentPaymentPage,
    setupTasks,
    adManagementTabsPage,
    userManagementPage,
  } = paths;

  const { mutate: trackEvent } = useRentalManagerUserTrackingV2WebApiHandler_event({
    meta: {
      context: { api: 'userTrackingEvent', details: {} },
    },
  });

  const { showLessenMaintenance, showLegacyMaintenanceTracker, isLessenMaintenanceActive } = getMaintenanceNavState({
    pathname,
    lessenMaintenancePage,
    displayMainMaintenanceTracker,
    isLessenMaintenanceEnabled,
  });

  const handleLinkClick = (pageClickingTo: string, csLink = CSTriggerSourceFromHeaderTitlesMap[pageClickingTo]) => {
    if (loggedIn && userModel) {
      if (pageClickingTo === HeaderTitles.payments && hasSeenNewPaymentsPage === false) {
        trackEvent({
          postData: { trackingEvent: RentalManagerUserTrackingEvent.OPEN_NEW_PAYMENTS_PAGE },
        });
      }

      if (csLink) {
        HeaderNavigationClickstreamEvents.ClickNavigationLink({ user: userModel, link: csLink });
      }
    }
  };

  const handleClickPropertyMenuItem = (event: React.SyntheticEvent) => {
    // prevent parent navigation
    event.stopPropagation();

    handleLinkClick(HeaderTitles.myProperties);
  };

  const { isInPropertiesSubtree, isPropertiesActive, isListingsActive } = getPortfolioNavState({
    pathname,
    propertiesPage,
    listingsPage,
    isListingsContext,
    isListingsNavEnabled,
  });
  const isPaymentPageActive = pathname.includes(payments) || pathname.includes(rentPayment);
  const financesItems = [
    {
      title: HeaderTitles.banking,
      path: bankingPage,
      fragment: banking,
      Filled: IconBankFilled,
      Outline: IconBankOutline,
    },
    {
      title: HeaderTitles.bookkeeping,
      path: bookkeepingPage,
      fragment: bookkeeping,
      Filled: IconCalculatorFilled,
      Outline: IconCalculatorOutline,
    },
  ];

  // Hold the list until every flag-gated item has resolved, so items never shift as answers arrive,
  // but stop waiting after a cap so a slow flag call only hides its own item, not the whole nav.
  const isNavPending = !hasNavWaitExpired && Boolean(loggedIn) && isLessenMaintenancePending;
  const expectedItemCount =
    [
      displayMainMyProperties,
      displayMainLeadManagement,
      !topBar && displayMainMessages,
      displayMainLeads,
      displayAnalytics,
      displayMainBilling,
      displayUserManagement,
      userModel.isEnterpriseCustomer,
      displayMainPayments,
      !topBar && displayMainNotificationCenter,
      displayOnboardingTasks,
    ].filter(Boolean).length + (displayMainBanking ? financesItems.length : 0);

  return (
    <Box
      data-testid="VerticalNavigation"
      css={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        padding: '200',
      }}
    >
      {/* logo (hidden when the IA top bar owns it) */}
      {!topBar && (
        <Box css={{ padding: '{spacing.800} 0 {spacing.800} {spacing.200}' }}>
          <AdaptiveAnchor
            path={propertiesPage}
            escape={escapeAllLinks}
            useAbsolutePaths={useAbsolutePaths}
            link={Link}
            title="Return to properties page"
          >
            <Box css={{ width: altLogo ? '200px' : '154px', height: 'auto' }}>
              {altLogo ? <LogoDesktop alternate={true} /> : <ZillowLogo showLegacyLogo={true} />}
            </Box>
          </AdaptiveAnchor>
        </Box>
      )}

      {isNavPending && (
        <VisuallyHidden asChild>
          <output>Loading navigation</output>
        </VisuallyHidden>
      )}

      <VerticalNav.Root
        outlined={false}
        elevated={false}
        css={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          marginBottom: '200',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <VerticalNav.List>
          {isNavPending ? (
            <NavigationSkeleton rows={expectedItemCount} />
          ) : (
            <>
              {/* dashboard */}
              {isListingsNavEnabled && (
                <NavigationItem
                  isActive={pathname.includes(dashboardPage)}
                  title={HeaderTitles.dashboard}
                  escape={true}
                  path={dashboardPage}
                  link={Link}
                  useAbsolutePaths={useAbsolutePaths}
                  ActiveIcon={<IconGridFilled />}
                  InactiveIcon={topBar ? <IconGridOutline /> : <IconGridFilled />}
                  TrailingIcon={null}
                  onClick={() => {
                    handleLinkClick(HeaderTitles.dashboard);
                  }}
                />
              )}

              {/* properties */}
              {loggedIn && displayMainMyProperties && (
                <NavigationItem
                  isActive={isPropertiesActive}
                  title={HeaderTitles.myProperties}
                  escape={escapeAllLinks}
                  path={propertiesPage}
                  link={Link}
                  useAbsolutePaths={useAbsolutePaths}
                  ActiveIcon={<IconHomeTypeFilled />}
                  InactiveIcon={topBar ? <IconHomeTypeOutline /> : <IconHomeTypeFilled />}
                  // TODO(ZRM-16762): the add-property "+" is to be removed for IA — hidden via topBar for now
                  TrailingIcon={
                    topBar ? null : (
                      <PlusIconPropertyMenu
                        Link={Link}
                        canAccessAddFeedListingsModal={canAccessAddFeedListingsModal}
                        escapeAllLinks={escapeAllLinks}
                        handleNoFeedIconPlusClick={handleLinkClick}
                        onClickPropertyMenuItem={handleClickPropertyMenuItem}
                        propertiesPage={propertiesPage}
                        useAbsolutePaths={useAbsolutePaths}
                        userModel={userModel}
                      />
                    )
                  }
                  onClick={() => {
                    handleLinkClick(HeaderTitles.myProperties);
                  }}
                />
              )}

              {/* listings */}
              {loggedIn && isListingsNavEnabled && (
                <NavigationItem
                  isActive={isListingsActive}
                  title={HeaderTitles.listings}
                  escape={true}
                  path={listingsPage}
                  link={Link}
                  useAbsolutePaths={useAbsolutePaths}
                  ActiveIcon={<IconYardSignFilled />}
                  InactiveIcon={topBar ? <IconYardSignOutline /> : <IconYardSignFilled />}
                  TrailingIcon={null}
                  onClick={() => {
                    handleLinkClick(HeaderTitles.listings);
                  }}
                />
              )}

              {/* leads */}
              {displayMainLeadManagement && (
                <NavigationItem
                  isActive={pathname.includes(leadManagementPage)}
                  title={HeaderTitles.leadManagement}
                  escape={true}
                  path={leadManagementPage}
                  link={Link}
                  useAbsolutePaths={useAbsolutePaths}
                  ActiveIcon={<IconUserGroupFilled />}
                  InactiveIcon={topBar ? <IconUserGroupOutline /> : <IconUserGroupFilled />}
                  TrailingIcon={null}
                  onClick={() => {
                    handleLinkClick(HeaderTitles.leadManagement);
                  }}
                />
              )}

              {/* messages (hidden when the IA top bar owns it) */}
              {!topBar && displayMainMessages && (
                <NavigationItem
                  isActive={pathname.includes(inboxPage)}
                  title={HeaderTitles.messages}
                  escape={true}
                  path={inboxPage}
                  link={Link}
                  useAbsolutePaths={useAbsolutePaths}
                  ActiveIcon={<IconMessageFilled />}
                  InactiveIcon={topBar ? <IconMessageOutline /> : <IconMessageFilled />}
                  TrailingIcon={null}
                  onClick={() => {
                    handleLinkClick(HeaderTitles.messages);
                  }}
                />
              )}

              {/* maintenance (Lessen pilot) */}
              {showLessenMaintenance && (
                <NavigationItem
                  isActive={isLessenMaintenanceActive}
                  title={HeaderTitles.lessenMaintenance}
                  escape={true}
                  path={lessenMaintenancePage}
                  newTagCookieName={LESSEN_MAINTENANCE_NEW_TAG_COOKIE}
                  newTagMaxAgeDays={NAV_NEW_TAG_MAX_AGE_DAYS}
                  link={Link}
                  useAbsolutePaths={useAbsolutePaths}
                  ActiveIcon={<IconHammerFilled />}
                  InactiveIcon={topBar ? <IconHammerOutline /> : <IconHammerFilled />}
                  TrailingIcon={null}
                  onClick={() => {
                    handleLinkClick(HeaderTitles.lessenMaintenance, LESSEN_MAINTENANCE_CS_TRIGGER_SOURCE);
                  }}
                />
              )}

              {/* maintenance (legacy tracker) */}
              {showLegacyMaintenanceTracker && (
                <NavigationItem
                  isActive={pathname.includes(maintenanceTrackerPage)}
                  title={HeaderTitles.maintenanceTracker}
                  escape={true}
                  path={maintenanceTrackerPage}
                  link={Link}
                  useAbsolutePaths={useAbsolutePaths}
                  ActiveIcon={<IconHammerFilled />}
                  InactiveIcon={topBar ? <IconHammerOutline /> : <IconHammerFilled />}
                  TrailingIcon={null}
                  onClick={() => {
                    handleLinkClick(HeaderTitles.maintenanceTracker);
                  }}
                />
              )}

              {/* contacts */}
              {displayMainLeads && (
                <NavigationItem
                  isActive={pathname.includes(leadsPage)}
                  title={HeaderTitles.leads}
                  escape={true}
                  path={leadsPage}
                  link={Link}
                  useAbsolutePaths={useAbsolutePaths}
                  ActiveIcon={<IconMessageFilled />}
                  InactiveIcon={topBar ? <IconMessageOutline /> : <IconMessageFilled />}
                  TrailingIcon={null}
                  onClick={() => {
                    handleLinkClick(HeaderTitles.leads);
                  }}
                />
              )}

              {/* analytics */}
              {displayAnalytics && (
                <NavigationItem
                  isActive={pathname.includes(analyticsPage)}
                  title={HeaderTitles.analytics}
                  escape={true}
                  path={`${analyticsPage}/${analyticsOverview}`}
                  link={Link}
                  useAbsolutePaths={useAbsolutePaths}
                  ActiveIcon={<IconTrendingFilled />}
                  InactiveIcon={topBar ? <IconTrendingOutline /> : <IconTrendingFilled />}
                  TrailingIcon={null}
                  onClick={() => {
                    handleLinkClick(HeaderTitles.analytics);
                  }}
                />
              )}

              {/* billing */}
              {displayMainBilling && (
                <NavigationItem
                  isActive={pathname.includes(billingPage)}
                  title={HeaderTitles.billing}
                  escape={true}
                  path={`${billingPage}/${billingDue}`}
                  link={Link}
                  useAbsolutePaths={useAbsolutePaths}
                  ActiveIcon={<IconCreditCardFilled />}
                  InactiveIcon={topBar ? <IconCreditCardOutline /> : <IconCreditCardFilled />}
                  TrailingIcon={null}
                  onClick={() => {
                    handleLinkClick(HeaderTitles.billing);
                  }}
                />
              )}

              {/* user management */}
              {displayUserManagement && (
                <NavigationItem
                  isActive={pathname.includes(userManagementPage)}
                  title={HeaderTitles.userManagement}
                  escape={true}
                  path={userManagementPage}
                  link={Link}
                  useAbsolutePaths={useAbsolutePaths}
                  ActiveIcon={<IconUserFilled />}
                  InactiveIcon={topBar ? <IconUserOutline /> : <IconUserFilled />}
                  TrailingIcon={null}
                  onClick={() => {
                    handleLinkClick(HeaderTitles.userManagement);
                  }}
                />
              )}

              {/* ad management */}
              {loggedIn && userModel.isEnterpriseCustomer && (
                <NavigationItem
                  isActive={pathname.includes(adManagementTabsPage)}
                  title={HeaderTitles.adManagement}
                  escape={true}
                  path={`${adManagementPage}/${adManagementTabsPage}`}
                  link={Link}
                  useAbsolutePaths={useAbsolutePaths}
                  ActiveIcon={<IconArchiveFilled />}
                  InactiveIcon={topBar ? <IconArchiveOutline /> : <IconArchiveFilled />}
                  TrailingIcon={null}
                  onClick={() => {
                    handleLinkClick(HeaderTitles.adManagement);
                  }}
                />
              )}

              {/* payments */}
              {loggedIn && displayMainPayments && (
                <NavigationItem
                  isActive={isInPropertiesSubtree ? false : isPaymentPageActive}
                  title={HeaderTitles.payments}
                  escape={true}
                  path={getPaymentsPath({
                    paymentsAcceptedCount,
                    paymentsAggregatePage,
                    paymentsGetStartedPage,
                    rentPaymentPage,
                  })}
                  link={Link}
                  useAbsolutePaths={useAbsolutePaths}
                  ActiveIcon={<IconDollarSignCircleFilled />}
                  InactiveIcon={topBar ? <IconDollarSignCircleOutline /> : <IconDollarSignCircleFilled />}
                  onClick={() => {
                    handleLinkClick(HeaderTitles.payments);
                  }}
                />
              )}

              {/* finances (Baselane banking and bookkeeping; reports follows in TM-2060) */}
              {loggedIn &&
                displayMainBanking &&
                financesItems.map(({ title, path, fragment, Filled, Outline }) => (
                  <NavigationItem
                    key={title}
                    isActive={pathname.includes(`/${fragment}`)}
                    title={title}
                    escape={true}
                    path={path}
                    link={Link}
                    useAbsolutePaths={useAbsolutePaths}
                    ActiveIcon={<Filled />}
                    InactiveIcon={topBar ? <Outline /> : <Filled />}
                    TrailingIcon={null}
                    onClick={() => {
                      handleLinkClick(title);
                    }}
                  />
                ))}

              {/* alerts (hidden when the IA top bar owns it) */}
              {!topBar && loggedIn && displayMainNotificationCenter && (
                <NavigationItem
                  isActive={false}
                  title={HeaderTitles.alerts}
                  escape={true}
                  path={null}
                  link={Link}
                  InactiveIcon={
                    <Box css={{ marginRight: '200' }}>
                      <NotificationCenter
                        useLeftNav={true}
                        isOpen={isAlertDropdownOpen}
                        onClose={() => setIsAlertDropdownOpen(false)}
                        onHasNewNotificationsChange={setHasNewNotifications}
                      />
                    </Box>
                  }
                  TrailingIcon={
                    hasNewNotifications && (
                      <Box css={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Box
                          data-testid="AlertsUnreadDot"
                          css={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            marginRight: '100',
                            height: '200',
                            width: '200',
                            borderRadius: 'ellipse',
                            backgroundColor: 'text.feedback.warning.hero',
                          }}
                        />
                      </Box>
                    )
                  }
                  onClick={(event: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => {
                    if (event) {
                      // do not navigate anywhere
                      event.preventDefault();
                      event.stopPropagation();
                    }

                    handleLinkClick(HeaderTitles.alerts);

                    // control the dropdown open state so that the entire item is clickable
                    if (isAlertDropdownOpen === false) {
                      setIsAlertDropdownOpen(true);
                    }

                    if (isAlertDropdownOpen === true) {
                      // use a slight delay when closing the dropdown
                      setTimeout(() => {
                        setIsAlertDropdownOpen(false);
                      }, 100);
                    }
                  }}
                />
              )}

              {displayOnboardingTasks && (
                <>
                  <Divider css={{ marginBlock: '400' }} />

                  {/* onboarding tasks */}
                  <NavigationItem
                    isActive={pathname.includes(setupTasks)}
                    title={HeaderTitles.onboardingTasks}
                    escape={true}
                    path={`${adManagementPage}/${setupTasks}`}
                    link={Link}
                    useAbsolutePaths={useAbsolutePaths}
                    ActiveIcon={<IconEditFormFilled />}
                    InactiveIcon={topBar ? <IconEditFormOutline /> : <IconEditFormFilled />}
                    TrailingIcon={null}
                    onClick={() => {
                      handleLinkClick(HeaderTitles.onboardingTasks);
                    }}
                  />
                </>
              )}
            </>
          )}
        </VerticalNav.List>
      </VerticalNav.Root>

      {!topBar && <AccountMenu displays={displays} paths={paths} userModel={userModel} />}
    </Box>
  );
};

export default memo(VerticalNavigation);
